import { useState, useEffect, useRef } from 'react'

export type RelevanceFilterType = 'all' | 'relevant' | 'non-relevant' | 'not-analyzed'

const FILTER_RELEVANT = 'relevant'
const FILTER_NON_RELEVANT = 'non-relevant'
const FILTER_NOT_ANALYZED = 'not-analyzed'

interface RelevanceFilterProps {
  value: RelevanceFilterType
  onChange: (value: RelevanceFilterType) => void
  counts?: {
    relevant: number
    nonRelevant: number
    notAnalyzed: number
    total: number
  }
}

const RELEVANT_JOBS_INFO_SHOWN_KEY = 'relevant-jobs-info-shown'
const NON_RELEVANT_JOBS_INFO_SHOWN_KEY = 'non-relevant-jobs-info-shown'
const NOT_ANALYZED_JOBS_INFO_SHOWN_KEY = 'not-analyzed-jobs-info-shown'

interface TooltipContent {
  title: string
  description: string
  items: string[]
}

const TOOLTIP_CONTENT: Record<RelevanceFilterType, TooltipContent | null> = {
  'all': null,
  [FILTER_RELEVANT]: {
    title: '💡 Relevant Jobs',
    description: 'These jobs have been identified as relevant based on your resume and job requirements. You can:',
    items: [
      'Select jobs and click "Prepare Application" to compare your resume with job requirements',
      'Get ATS matching analysis to improve your application',
      'Adapt your resume with keyword suggestions',
      'Export optimized resume as PDF'
    ]
  },
  [FILTER_NON_RELEVANT]: {
    title: '📋 Non-Relevant Jobs',
    description: 'These jobs have been marked as not relevant. You can:',
    items: [
      'Review matching rules to improve relevance detection',
      'Mark jobs as relevant if they were incorrectly classified',
      'Analyze jobs to get AI-powered relevance assessment'
    ]
  },
  [FILTER_NOT_ANALYZED]: {
    title: '🔍 Not Analyzed Jobs',
    description: 'These jobs haven\'t been analyzed for relevance yet. You can:',
    items: [
      'Select jobs and click "Analyze Relevance" to get AI assessment',
      'Jobs will be automatically classified as relevant or non-relevant',
      'Use this to quickly identify the best opportunities'
    ]
  }
}

function RelevanceFilter({ value, onChange, counts }: RelevanceFilterProps) {
  const [hoveredOption, setHoveredOption] = useState<RelevanceFilterType | null>(null)
  const [autoShownTooltip, setAutoShownTooltip] = useState<RelevanceFilterType | null>(null)
  const buttonRefs = useRef<Record<RelevanceFilterType, HTMLButtonElement | null>>({
    'all': null,
    [FILTER_RELEVANT]: null,
    [FILTER_NON_RELEVANT]: null,
    [FILTER_NOT_ANALYZED]: null
  })
  const tooltipRefs = useRef<Record<RelevanceFilterType, HTMLDivElement | null>>({
    'all': null,
    [FILTER_RELEVANT]: null,
    [FILTER_NON_RELEVANT]: null,
    [FILTER_NOT_ANALYZED]: null
  })
  const autoShowTimerRef = useRef<NodeJS.Timeout | null>(null)

  const options: { value: RelevanceFilterType; label: string; count?: number }[] = [
    { value: 'all', label: 'All Jobs', count: counts?.total },
    { value: FILTER_RELEVANT, label: 'Relevant', count: counts?.relevant },
    { value: FILTER_NON_RELEVANT, label: 'Non-Relevant', count: counts?.nonRelevant },
    { value: FILTER_NOT_ANALYZED, label: 'Not Analyzed', count: counts?.notAnalyzed },
  ]

  // Проверяем, какие tooltips уже показывались
  const getInfoShownKey = (optionValue: RelevanceFilterType): string => {
    switch (optionValue) {
      case FILTER_RELEVANT:
        return RELEVANT_JOBS_INFO_SHOWN_KEY
      case FILTER_NON_RELEVANT:
        return NON_RELEVANT_JOBS_INFO_SHOWN_KEY
      case FILTER_NOT_ANALYZED:
        return NOT_ANALYZED_JOBS_INFO_SHOWN_KEY
      default:
        return ''
    }
  }

  // Автоматический показ tooltip при первом входе на вкладку
  useEffect(() => {
    const optionValue = value
    const tooltipContent = TOOLTIP_CONTENT[optionValue]
    
    if (tooltipContent) {
      const hasShown = localStorage.getItem(getInfoShownKey(optionValue)) === 'true'
      if (!hasShown) {
        // Показываем tooltip автоматически через небольшую задержку
        autoShowTimerRef.current = setTimeout(() => {
          setHoveredOption(optionValue)
          setAutoShownTooltip(optionValue)
          localStorage.setItem(getInfoShownKey(optionValue), 'true')
        }, 800)

        return () => {
          if (autoShowTimerRef.current) {
            clearTimeout(autoShowTimerRef.current)
          }
        }
      }
    } else {
      // Закрываем tooltip при переключении на вкладку без tooltip
      if (autoShowTimerRef.current) {
        clearTimeout(autoShowTimerRef.current)
      }
      if (autoShownTooltip) {
        setHoveredOption(null)
        setAutoShownTooltip(null)
      }
    }
  }, [value])

  // Закрытие tooltip при клике вне его (только если это автоматический показ)
  useEffect(() => {
    if (!autoShownTooltip) return

    const handleClickOutside = (event: MouseEvent) => {
      const tooltipRef = tooltipRefs.current[autoShownTooltip]
      const buttonRef = buttonRefs.current[autoShownTooltip]
      
      if (
        tooltipRef &&
        !tooltipRef.contains(event.target as Node) &&
        buttonRef &&
        !buttonRef.contains(event.target as Node)
      ) {
        setHoveredOption(null)
        setAutoShownTooltip(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [autoShownTooltip])

  const handleMouseEnter = (optionValue: RelevanceFilterType) => {
    if (TOOLTIP_CONTENT[optionValue]) {
      setHoveredOption(optionValue)
    }
  }

  const handleMouseLeave = (optionValue: RelevanceFilterType) => {
    // Не закрываем tooltip, если это автоматический показ для этой опции
    if (autoShownTooltip !== optionValue) {
      setHoveredOption(null)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-700 mr-2">Relevance:</span>
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {options.map((option) => {
          const isActive = value === option.value
          const tooltipContent = TOOLTIP_CONTENT[option.value]
          const showTooltip = hoveredOption === option.value && tooltipContent !== null
          const isAutoShown = autoShownTooltip === option.value
          
          return (
            <div key={option.value} className="relative">
              <button
                ref={(el) => {
                  buttonRefs.current[option.value] = el
                }}
                onClick={() => onChange(option.value)}
                onMouseEnter={() => handleMouseEnter(option.value)}
                onMouseLeave={() => handleMouseLeave(option.value)}
                className={`
                  px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                  ${isActive
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
              >
                {option.label}
                {option.count !== undefined && (option.count > 0 || option.value === FILTER_NOT_ANALYZED) && (
                  <span className={`ml-1.5 ${isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                    ({option.count})
                  </span>
                )}
              </button>
              
              {/* Tooltip для кнопок с описанием */}
              {showTooltip && tooltipContent && (
                <div
                  ref={(el) => {
                    tooltipRefs.current[option.value] = el
                  }}
                  className="absolute left-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4"
                  style={{ 
                    marginLeft: '0',
                    width: 'max-content',
                    minWidth: 'fit-content',
                    maxWidth: '90vw'
                  }}
                  onMouseEnter={() => setHoveredOption(option.value)}
                  onMouseLeave={() => {
                    if (!isAutoShown) {
                      setHoveredOption(null)
                    }
                  }}
                >
                  <div className="text-sm text-gray-900">
                    <p className="font-semibold mb-2 whitespace-nowrap">{tooltipContent.title}</p>
                    <p className="text-gray-600 mb-3 whitespace-nowrap">
                      {tooltipContent.description}
                    </p>
                    <ul className="list-disc space-y-1 text-gray-600" style={{ paddingLeft: '1.25rem' }}>
                      {tooltipContent.items.map((item, index) => (
                        <li key={index} className="whitespace-nowrap">{item}</li>
                      ))}
                    </ul>
                  </div>
                  {isAutoShown && (
                    <button
                      onClick={() => {
                        setHoveredOption(null)
                        setAutoShownTooltip(null)
                      }}
                      className="mt-3 text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
                    >
                      Got it
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RelevanceFilter
